import { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import useOrgStore from '../store/zustand/useOrgStore';
import { updateOrg } from 'graphql/mutations';

export default function useOrgData() {
  // @ts-ignore
  const orgID = (global as any)?.me?.orgID;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const setOrgData = useOrgStore((state) => state.setOrgData);
  const orgData = useOrgStore((state) => state.orgData);

  useEffect(() => {
    const fetchAndStoreOrgData = async () => {
      // Only fetch if we don't have data yet and we have an orgID
      if (orgData || !orgID) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const myQuery = gql`
          query GetOrg($id: ID!) {
            getOrg(id: $id) {
              id
              name
              configParms
              contactName
              contactPhone
              contactEmail
              contactAddress
              contactAddress2
              contactCity
              contactState
              contactZip
              docuSealAccess
              companyLogo
              coverPhoto
              legalBusinessName
              dbaName
              legalEntityType
              einTaxId
              statesOfOperation
              insuranceContactName
              insuranceContactTitle
              insuranceContactPhone
              insuranceContactEmail
              licenseNumber
              licenseIssuingState
              licenseExpirationDate
              defaultEstimateDisclaimer
              standardScopeNotes
              clonedFrom
              billingType
            }
          }
        `;

        // @ts-ignore
        const res: any = await API.graphql({
          // @ts-ignore
          query: myQuery,
          variables: { id: orgID },
        });

        const fetchedOrgData = res.data.getOrg;

        if (fetchedOrgData?.configParms) {
          try {
            const parsedConfigParms = JSON.parse(fetchedOrgData.configParms);
            setOrgData({
              ...fetchedOrgData,
              configParms: parsedConfigParms,
            });
          } catch (parseError) {
            console.error('Error parsing configParms:', parseError);
            setOrgData(fetchedOrgData);
          }
        } else {
          setOrgData(fetchedOrgData);
        }
      } catch (err) {
        console.error('Error fetching org data:', err);
        setError('Failed to fetch organization data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndStoreOrgData();
  }, [orgID, orgData, setOrgData]);

  const refetchOrgData = async () => {
    if (!orgID) return;

    try {
      setIsLoading(true);
      setError(null);

      const myQuery = gql`
        query GetOrg($id: ID!) {
          getOrg(id: $id) {
            id
            name
            configParms
            contactName
            contactPhone
            contactEmail
            contactAddress
            contactAddress2
            contactCity
            contactState
            contactZip
            docuSealAccess
            companyLogo
            coverPhoto
            legalBusinessName
            dbaName
            legalEntityType
            einTaxId
            statesOfOperation
            insuranceContactName
            insuranceContactTitle
            insuranceContactPhone
            insuranceContactEmail
            licenseNumber
            licenseIssuingState
            licenseExpirationDate
            defaultEstimateDisclaimer
            standardScopeNotes
            clonedFrom
            billingType
          }
        }
      `;

      // @ts-ignore
      const res: any = await API.graphql({
        // @ts-ignore
        query: myQuery,
        variables: { id: orgID },
      });

      const fetchedOrgData = res.data.getOrg;

      if (fetchedOrgData?.configParms) {
        try {
          const parsedConfigParms = JSON.parse(fetchedOrgData.configParms);
          setOrgData({
            ...fetchedOrgData,
            configParms: parsedConfigParms,
          });
        } catch (parseError) {
          console.error('Error parsing configParms:', parseError);
          setOrgData(fetchedOrgData);
        }
      } else {
        setOrgData(fetchedOrgData);
      }
    } catch (err) {
      console.error('Error refetching org data:', err);
      setError('Failed to refetch organization data');
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrgData = async (orgDataInput: any) => {
    try {
      await API.graphql(graphqlOperation(updateOrg, { input: orgDataInput }));
      // if configParms is JSON string, parse it
      if (
        orgDataInput.configParms &&
        typeof orgDataInput.configParms === 'string'
      ) {
        orgDataInput.configParms = JSON.parse(orgDataInput.configParms);
      }

      // Get the current state from the store to ensure we have the latest
      const currentOrgData = useOrgStore.getState().orgData;
      setOrgData({ ...currentOrgData, ...orgDataInput });

      return true;
    } catch (err) {
      console.error('Error updating org data:', err);
      return false;
    }
  };
  return { isLoading, error, orgData, updateOrgData, refetchOrgData };
}
